import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Funnel } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export function StockPositionFiltersModal({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  productOptions,
  warehouseOptions,
  categoryOptions,
}) {
  const { t } = useTranslation();
  const [tempFilters, setTempFilters] = useState({
    productId: "All",
    warehouse: "All",
    category: "All",
    deadStock: "All",
  });

  // Load applied filters when modal opens
  useEffect(() => {
    if (open) {
      setTempFilters({
        productId: appliedFilters.productId || "All",
        warehouse: appliedFilters.warehouse || "All",
        category: appliedFilters.category || "All",
        deadStock: appliedFilters.deadStock || "All",
      });
    }
  }, [open, appliedFilters]);

  const handleChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply(tempFilters);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        {/* Backdrop overlay */}
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-sm fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[450px]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-5 font-sans focus:outline-none",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Funnel className="h-4.5 w-4.5 text-[var(--color-primary)]" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.filterStockPosition")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close className="h-8 w-8 grid place-items-center rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition focus:outline-none">
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          {/* Form Content */}
          <div className="space-y-4">
            {/* Product Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">{t("inventory.product")}</label>
              <Select
                value={tempFilters.productId}
                onValueChange={(val) => handleChange("productId", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                  <SelectValue placeholder={t("inventory.allProducts")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t("inventory.allProducts")}</SelectItem>
                  {productOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Warehouse Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">{t("inventory.warehouse")}</label>
              <Select
                value={tempFilters.warehouse}
                onValueChange={(val) => handleChange("warehouse", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                  <SelectValue placeholder={t("inventory.allWarehouses")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t("inventory.allWarehouses")}</SelectItem>
                  {warehouseOptions.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">{t("common.category")}</label>
              <Select
                value={tempFilters.category}
                onValueChange={(val) => handleChange("category", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                  <SelectValue placeholder={t("inventory.allCategories")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t("inventory.allCategories")}</SelectItem>
                  {categoryOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dead Stock Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-foreground">{t("inventory.deadStock")}</label>
              <Select
                value={tempFilters.deadStock}
                onValueChange={(val) => handleChange("deadStock", val)}
              >
                <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                  <SelectValue placeholder={t("inventory.allStock")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t("inventory.allStock")}</SelectItem>
                  <SelectItem value="Dead Stock">{t("inventory.deadStock")}</SelectItem>
                  <SelectItem value="Active Stock">{t("inventory.activeStock")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-[var(--color-text-border)]/50 shrink-0">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
            >
              {t("formActions.cancel")}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="h-10 px-5 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer"
            >
              {t("formActions.applyFilters")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
